/**
 * Parse fetch request body
 * @param body BodyInit of the fetch
 * @param headers headers objets to detect if body parsed as text needs json parsing
 * @returns promise of a parsed body of type string or json object
 */
export async function parseFetchRequestBody(
  body: BodyInit | undefined,
  headers: Record<string, string> | undefined
): Promise<string | object> {
  if (!body) {
    return undefined;
  }
  const parsedBodyInit = await parseBodyInit(body);
  if (typeof parsedBodyInit === "string" && isJsonContentType(headers)) {
    try {
      return JSON.parse(parsedBodyInit);
    } catch {
      return parsedBodyInit;
    }
  }

  return parsedBodyInit;
}

/**
 * Parse fetch request input
 * @param request fetch request, a copy made with request.clone() should be passed
 * @param headers headers object to detect if parsed request is json
 * @returns promise of a parsed request of type string or json object
 */
export async function parseFetchRequestInput(
  request: Request,
  headers: Record<string, string>
): Promise<string | object> {
  const text = await request.text();
  if (isJsonContentType(headers)) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  return text;
}

const isJsonContentType = (headers?: Record<string, string>) => {
  return headers?.["content-type"]?.toLowerCase().includes("application/json");
};

async function parseBodyInit(body: BodyInit) {
  if (body instanceof File) {
    return {
      file: "REDACTED",
      name: body.name,
      size: body.size,
      type: body.type,
    };
  }
  if (body instanceof ReadableStream) {
    const reader = new Response(body);

    return await reader.text();
  }
  if (body instanceof Blob) {
    return body.text();
  }
  if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) {
    const decoder = new TextDecoder("utf-8");

    return decoder.decode(body);
  }
  if (body instanceof FormData) {
    const result = {};
    for (const [key, value] of body) {
      result[key] = value;
    }

    return result;
  }
  if (body instanceof URLSearchParams) {
    const result = {};
    for (const [key, value] of body) {
      result[key] = value;
    }

    return result;
  }
  if (typeof body === "string") {
    return body;
  }

  return undefined;
}
